import { g, layers } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { getMaskBounds } from './mask-logic';
import { SelectionTransformUI } from './selection-transform-ui';
import { getFC, setFloatingContent, RasterTransformAction } from './raster-transform-action';

/**
 * @file transformer.ts
 * @description Core logic for moving and transforming selection content
 * Maintains backward compatibility with existing API
 */

export function moveSelectionContent(dx: number, dy: number, skipErase: boolean = false) {
    if (!g.isSelectionActive || !g.selectionCanvas) return;

    // Check both local 'g' and 'window' to be extra safe against double-erasing
    let fc = getFC();

    if (!fc) {
        const activeLayer = layers[g.activeLayerIndex];
        if (!activeLayer) return;

        let transformer = (window as any).transformUI as SelectionTransformUI;
        if (!transformer) {
            transformer = new SelectionTransformUI();
            (window as any).transformUI = transformer;
        }

        transformer.originalShapes = activeLayer.shapes ? [...activeLayer.shapes] : [];
        transformer.originalImageData = (activeLayer.ctx as CanvasRenderingContext2D).getImageData(0, 0, activeLayer.canvas.width, activeLayer.canvas.height);
        transformer.originalLayerIndex = g.activeLayerIndex;

        const bounds = getMaskBounds(g.selectionCanvas as HTMLCanvasElement);
        if (!bounds) return;

        const fCanvas = g.createCanvas(bounds.w, bounds.h) as HTMLCanvasElement;
        const fctx = fCanvas.getContext("2d");

        const tempCanvas = g.createCanvas(g.image_width, g.image_height) as HTMLCanvasElement;
        const tctx = tempCanvas.getContext("2d");
        
        if (tctx && fctx) {
            // STEP 1: Extract the pixels from source layer
            tctx.drawImage(activeLayer.canvas as HTMLCanvasElement, 0, 0);
            tctx.globalCompositeOperation = "destination-in";
            tctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            fctx.drawImage(tempCanvas, -bounds.x, -bounds.y);

            // STEP 2: Erase original pixels ONLY if not in copy mode AND no floating content already exists
            if (!skipErase) {
                const lctx = activeLayer.ctx as CanvasRenderingContext2D;
                lctx.save();
                lctx.globalCompositeOperation = "destination-out";
                lctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
                lctx.restore();
            }

            // STEP 3: Initialize floating content state
            fc = {
                canvas: fCanvas,
                x: bounds.x,
                y: bounds.y,
                angle: 0,
                scaleX: 1,
                scaleY: 1,
                originalLayerIndex: g.activeLayerIndex
            };
            setFloatingContent(fc);

            transformer.show(bounds);
        }
    }

    // STEP 4: Update position if we have content
    if (fc) {
        // Validation check for fc properties to prevent crashes in updatePosition
        if (typeof fc.x !== 'number') fc.x = 0;
        if (typeof fc.y !== 'number') fc.y = 0;

        fc.x += dx;
        fc.y += dy;
        
        EventBus.emit('MOVE_SELECTION_MASK', { dx, dy });

        const transformer = (window as any).transformUI as SelectionTransformUI;
        if (transformer) {
            transformer.updatePosition();
            if (!transformer.active) {
                const bounds = getMaskBounds(g.selectionCanvas as HTMLCanvasElement);
                if (bounds) transformer.show(bounds);
            }
        }
        
        // Ensure state sticks across both sources
        setFloatingContent(fc);
        
        // Update Status Bar & Properties Panel
        EventBus.emit('DRAWING_STATUS_UPDATE');
        
        // Sync selection mask with transformation parameters
        EventBus.emit('TRANSFORM_SELECTION_MASK', { 
            x: fc.x, 
            y: fc.y, 
            angle: fc.angle, 
            scaleX: fc.scaleX, 
            scaleY: fc.scaleY,
            originalBounds: transformer?.originalBounds
        });
    }
}

export function finalizeMoveContent() {
    const fc = getFC();
    if (!fc) return;
    
    const activeLayer = layers[g.activeLayerIndex];
    if (activeLayer) {
        const transformer = (window as any).transformUI as SelectionTransformUI;
        const beforeImageData = transformer?.originalImageData;
        const beforeShapes = transformer?.originalShapes;

        const lctx = activeLayer.ctx as CanvasRenderingContext2D;
        const centerX = fc.x + fc.canvas.width / 2;
        const centerY = fc.y + fc.canvas.height / 2;
        
        lctx.save();
        lctx.imageSmoothingEnabled = g.imageSmoothingEnabled;
        lctx.translate(centerX, centerY);
        if (fc.angle) lctx.rotate(fc.angle);
        if (fc.scaleX !== undefined && fc.scaleY !== undefined) {
            lctx.scale(fc.scaleX, fc.scaleY);
        }
        lctx.drawImage(fc.canvas, -fc.canvas.width / 2, -fc.canvas.height / 2);
        lctx.restore();
        
        // Push to History
        const afterImageData = lctx.getImageData(0, 0, activeLayer.canvas.width, activeLayer.canvas.height);
        const historyManager = (window as any).historyManager;
        if (historyManager && beforeImageData) {
            historyManager.push(new RasterTransformAction(
                "Raster Transform",
                g.activeLayerIndex,
                beforeImageData,
                afterImageData,
                beforeShapes ? [...beforeShapes] : undefined,
                activeLayer.shapes ? [...activeLayer.shapes] : undefined
            ));
        }
    }
    
    setFloatingContent(null);
    EventBus.emit('RENDER_LAYERS');
}

// Backward compatibility exports
export { SelectionTransformUI } from './selection-transform-ui';
export { RasterTransformAction, getFC, setFloatingContent } from './raster-transform-action';